import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CalendarDays, MapPin, Wifi, Clock } from "lucide-react";
import { formatDate, pillarLabel, pillarColor } from "../lib/content";

export function Chip({ pillar }) {
  return (
    <span className="inline-flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.15em] text-ink-2">
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: pillarColor(pillar) }} />
      {pillarLabel(pillar)}
    </span>
  );
}

export function SampleTag() {
  return (
    <span
      data-testid="sample-tag"
      className="inline-block text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ink-2 border border-line rounded-full px-2.5 py-1 bg-sand-2"
    >
      Sample
    </span>
  );
}

export function EventCard({ event, past = false }) {
  return (
    <Link
      to={`/events/${event.slug}`}
      data-testid={`event-card-${event.slug}`}
      className={`group flex flex-col border border-line bg-sand overflow-hidden rounded-xl transition-colors duration-500 hover:bg-sand-2 ${past ? "opacity-80" : ""}`}
    >
      <div className="relative overflow-hidden aspect-[16/10]">
        <img
          src={event.image}
          alt={event.imageAlt}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute top-4 left-4 flex gap-2">
          <span className="bg-sand/90 backdrop-blur px-3 py-1.5 rounded-full text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-ink">
            {pillarLabel(event.pillar)}
          </span>
          {event.isSample && <SampleTag />}
        </div>
      </div>
      <div className="flex flex-col gap-3 p-6 md:p-8 grow">
        <h3 className="font-serif text-2xl md:text-[1.7rem] leading-tight tracking-tight text-ink">
          {event.title}
        </h3>
        <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-ink-2">
          <span className="inline-flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            {formatDate(event.startDate, { weekday: "short" })}
            {event.endDate ? ` — ${formatDate(event.endDate)}` : ""}
          </span>
          <span className="inline-flex items-center gap-2">
            {event.isOnline ? <Wifi className="h-4 w-4" /> : <MapPin className="h-4 w-4" />}
            {event.isOnline ? "Online" : event.location}
          </span>
        </div>
        <p className="text-sm leading-relaxed text-ink-2 line-clamp-2">{event.description}</p>
        <span className="mt-auto pt-3 inline-flex items-center gap-2 text-sm font-semibold text-action">
          {past ? "View recap" : event.ctaLabel}
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
    </Link>
  );
}

export function StoryCard({ story, featured = false, withImage = false }) {
  const showImage = featured || withImage;
  return (
    <Link
      to={`/stories/${story.slug}`}
      data-testid={`story-card-${story.slug}`}
      className={`group flex flex-col ${showImage ? "" : "border-t border-line pt-6"}`}
    >
      {showImage && (
        <div className={`relative overflow-hidden rounded-xl mb-6 ${featured ? "aspect-[16/10]" : "aspect-[16/9]"}`}>
          <img
            src={story.image}
            alt={story.imageAlt}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
        </div>
      )}
      <div className="flex items-center gap-3">
        <Chip pillar={story.category} />
        {story.isSample && <SampleTag />}
      </div>
      <h3
        className={`mt-3 font-serif tracking-tight leading-tight text-ink group-hover:underline underline-offset-4 decoration-1 ${
          featured ? "text-3xl md:text-4xl" : "text-2xl"
        }`}
      >
        {story.title}
      </h3>
      <p className={`mt-3 text-sm leading-relaxed text-ink-2 ${featured ? "" : "line-clamp-2"}`}>
        {story.excerpt}
      </p>
      <p className="mt-4 flex items-center gap-4 text-xs text-ink-2">
        <span className="inline-flex items-center gap-1.5">
          <CalendarDays className="h-3.5 w-3.5" /> {formatDate(story.publishedAt)}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" /> {story.readingTime} min read
        </span>
      </p>
    </Link>
  );
}

export function ActivityCard({ activity, featured = false }) {
  if (featured) {
    return (
      <div
        data-testid={`activity-card-${activity.slug}`}
        className="group grid grid-cols-1 md:grid-cols-2 overflow-hidden rounded-xl border border-line bg-sand"
      >
        <Link
          to={`/activities/${activity.slug}`}
          aria-label={`${activity.title} — view activity`}
          className="relative block overflow-hidden aspect-[16/10] md:aspect-auto md:min-h-[340px]"
        >
          <img
            src={activity.image}
            alt={activity.imageAlt}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
          <div className="absolute top-4 left-4 flex gap-2">
            <span className="bg-sand/90 backdrop-blur px-3 py-1.5 rounded-full text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-ink">
              {pillarLabel(activity.pillar)}
            </span>
            <span className="bg-action/90 backdrop-blur px-3 py-1.5 rounded-full text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-sand">
              {activity.status}
            </span>
            {activity.isSample && <SampleTag />}
          </div>
        </Link>
        <div className="flex flex-col gap-4 p-8 md:p-12 justify-center">
          <h3 className="font-serif text-3xl md:text-4xl tracking-tight text-ink">
            <Link to={`/activities/${activity.slug}`} className="hover:underline underline-offset-4 decoration-1">
              {activity.title}
            </Link>
          </h3>
          <p className="text-sm md:text-base leading-relaxed text-ink-2">{activity.summary}</p>
          {activity.externalUrl ? (
            <a
              href={activity.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${activity.ctaLabel} (opens in new tab)`}
              data-testid={`activity-card-cta-${activity.slug}`}
              className="group mt-2 inline-flex w-full sm:w-auto items-center justify-center gap-2 self-start rounded-full bg-action text-sand px-7 py-3.5 text-sm font-semibold transition-transform duration-300 hover:scale-[1.04] active:scale-95 hover:bg-action-hover"
            >
              {activity.ctaLabel}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          ) : (
            <Link
              to={`/activities/${activity.slug}`}
              className="group mt-2 inline-flex w-full sm:w-auto items-center justify-center gap-2 self-start rounded-full bg-action text-sand px-7 py-3.5 text-sm font-semibold transition-transform duration-300 hover:scale-[1.04] active:scale-95 hover:bg-action-hover"
            >
              {activity.ctaLabel}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          )}
        </div>
      </div>
    );
  }
  return (
    <Link
      to={`/activities/${activity.slug}`}
      data-testid={`activity-card-${activity.slug}`}
      className="group relative flex flex-col overflow-hidden rounded-xl border border-line bg-sand"
    >
      <div className="relative overflow-hidden aspect-[4/3]">
        <img
          src={activity.image}
          alt={activity.imageAlt}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-ink/50 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
          <h3 className="font-serif text-2xl md:text-3xl tracking-tight text-sand">{activity.title}</h3>
        </div>
        <div className="absolute top-4 left-4 flex gap-2">
          <span className="bg-sand/90 backdrop-blur px-3 py-1.5 rounded-full text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-ink">
            {pillarLabel(activity.pillar)}
          </span>
          <span className="bg-action/90 backdrop-blur px-3 py-1.5 rounded-full text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-sand">
            {activity.status}
          </span>
          {activity.isSample && <SampleTag />}
        </div>
      </div>
      <div className="flex flex-col gap-4 p-6 md:p-8 grow">
        <p className="text-sm leading-relaxed text-ink-2">{activity.summary}</p>
        <span className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-action">
          {activity.ctaLabel}
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
    </Link>
  );
}
